import React, { memo, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { paymentViewSelector } from '@/domain/env';

import Page from '@/pages/dokkaPay/pages/payment/components/PaymentComponent/components/Page';
import PasswordForm from '@/pages/document/DocumentViewer/PasswordForm';
import PdfDocumentBase from '@/components/PdfDocument';

// JS component props (inputRef, width) not in TypeScript types
const PdfDocument = PdfDocumentBase as React.ComponentType<any>;
import ProgressBar from '@/pages/document/components/ProgressBar';
import DocumentNavBar from '@/pages/document/DocumentPagesNavigationBar';
import { PDFRoot, PDFPages, PDFPage } from '@/pages/dokkaPay/pages/payment/components/PaymentComponent/StyledComponents';

import usePaymentHook from '@/pages/dokkaPay/pages/payment/components/PaymentComponent/usePaymentHook';
import usePreloadPayments from '@/pages/dokkaPay/pages/payment/components/PaymentComponent/usePreloadPayments';

interface PortalProps {
  children: React.ReactNode;
}

const PreviewPortalLeft: React.FC<PortalProps> = ({ children }) => {
  const el = useRef<HTMLElement | null>(null);
  const current = el.current || window.document.getElementById('doc-preview');
  if (!el.current) {
    el.current = current;
  }
  return el.current ? ReactDOM.createPortal(children, el.current) : null;
};

const PreviewPortalRight: React.FC<PortalProps> = ({ children }) => {
  const el = useRef<HTMLElement | null>(null);
  const current = el.current || window.document.getElementById('doc-preview-rtl');
  if (!el.current) {
    el.current = current;
  }
  return el.current ? ReactDOM.createPortal(children, el.current) : null;
};

const portals: Record<string, React.FC<PortalProps>> = {
  left: PreviewPortalLeft,
  right: PreviewPortalRight,
};

const mapStateToProps = (state: unknown) => ({
  paymentsWidth: (paymentViewSelector as (s: unknown) => { get: (k: string) => unknown })(state).get('payPaymentsWidth'),
});

interface PaymentComponentProps {
  children?: React.ReactNode;
  width: number;
  startResizeWidth: number;
  scale: number;
  onScale: (scale: number) => void;
  getIsDisabledZoomButton: (type: string) => boolean;
  onZoom: (type: string) => void;
  onReset: () => void;
  currentSidebar: string;
}

// eslint-disable-next-line react/function-component-definition
const PaymentComponent: React.FC<PaymentComponentProps> = ({
  children,
  width,
  startResizeWidth,
  scale,
  onScale,
  getIsDisabledZoomButton,
  onZoom,
  onReset,
  currentSidebar,
}) => {
  const paymentContainerRef = useRef<HTMLDivElement | null>(null);
  const { paymentId = '' } = useParams();
  const { paymentsWidth } = useSelector(mapStateToProps);

  const {
    selectPage,
    pageParams,
    password,
    passwordError,
    onPasswordEnter,
    onDocumentLoadError,
    frameResizeScale,
    isProtected,
    isPageShow,
    viewportWidth,
    handleViewportRef,
    handlePageRef,
    handleRotate,
    scrollViewRef,
    renderCancelable,
    loadCancelable,
    setPasswordError,
    onScroll,
    focusedPage,
    isPreviewOpen,
    setPreviewOpen,
  } = usePaymentHook({
    paymentId,
    width,
    startResizeWidth,
    scale,
  });

  const cachedDocument = usePreloadPayments({
    currentPaymentId: paymentId,
  });

  const onClosePreview = useCallback(() => setPreviewOpen(false), [setPreviewOpen]);
  const Portal = portals[currentSidebar];

  return isProtected || passwordError ? (
    <>
      <Portal>{children}</Portal>
      <PasswordForm error={passwordError} paymentsWidth={paymentsWidth} onPasswordEnter={onPasswordEnter} />
    </>
  ) : (
    <PDFRoot ref={paymentContainerRef}>
      {pageParams.get(0)?.status !== 'complete' && <ProgressBar value={cachedDocument.downloadProgress} />}
      {pageParams.size > 1 ? (
        <Portal>
          <DocumentNavBar
            file={cachedDocument.blob}
            pageParams={pageParams}
            onSelect={selectPage}
            focusedPage={focusedPage}
            isOpen={isPreviewOpen}
            setOpen={setPreviewOpen}
            password={password}
          >
            {children}
          </DocumentNavBar>
        </Portal>
      ) : (
        <Portal>{children}</Portal>
      )}
      {cachedDocument.blob && (
        <PdfDocument
          file={cachedDocument.blob}
          inputRef={handleViewportRef}
          onLoadError={onDocumentLoadError}
          onLoadSuccess={loadCancelable.execute}
          loading={<div />}
          width={300}
          onPassword={(cp: (password: string) => void, reason: number) => {
            // NEED_PASSWORD: 1,
            // INCORRECT_PASSWORD: 2
            if (reason === 2) {
              setPasswordError(true);
            } else {
              cp(password);
            }
          }}
        >
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions,jsx-a11y/click-events-have-key-events */}
          <PDFPages onScroll={onScroll} onClick={onClosePreview}>
            {pageParams.map((params, idx) => (
              <PDFPage
                key={`${paymentId}-${idx.toString()}`}
                ref={handlePageRef(idx)}
                data-attr={`${paymentId}-${idx}`}
                style={{ zoom: frameResizeScale }}
              >
                {isPageShow(params) ? (
                  <Page
                    idx={params.idx}
                    total={pageParams.size}
                    paymentId={paymentId}
                    width={viewportWidth}
                    params={params}
                    scale={scale}
                    onScale={onScale}
                    onRenderSuccess={renderCancelable.execute}
                    onRotate={handleRotate}
                    container={scrollViewRef}
                    getIsDisabledZoomButton={getIsDisabledZoomButton}
                    onZoom={onZoom}
                    onReset={onReset}
                  />
                ) : null}
              </PDFPage>
            ))}
          </PDFPages>
        </PdfDocument>
      )}
    </PDFRoot>
  );
};

export default memo(PaymentComponent);
